<template>
  <tokens-bar
    v-model:search="searchCriteria"
    v-model:format="format"
    v-model:mode="mode"
    v-model:theme="theme"
    @filter="filterTokens"
  />
  <dt-empty-state
    v-if="noSearchResults"
    :size="200"
    :header-text="`No results found for &OpenCurlyDoubleQuote;${searchCriteria}&CloseCurlyDoubleQuote;`"
    class="d-w100p d-ba d-bc-subtle d-bar-400 d-mbs-200 d-pbs-400"
  >
    <template #icon="{ iconSize }">
      <dt-icon name="box" :size="iconSize" />
    </template>
  </dt-empty-state>
  <token-tree v-else :node="filteredTokens" :category="null" :level="2" :mode="mode" />
</template>

<script setup>
import { capitalize, computed, ref, shallowRef, onBeforeMount, inject } from 'vue';
import TokenTree from './TokenTree.vue';
import TokensBar from './TokensBar.vue';
import { addTokensToStructure } from './utilities';
import { useRoute } from 'vue-router';

const route = useRoute();
const format = ref(route.query.format || 'CSS');
const mode = ref(route.query.mode || 'light');
const theme = ref(route.query.theme || 'dp');
const searchCriteria = ref(route.query.search || null);
const processedTokens = {}; // is set beforeMount and never changes
const filteredTokens = shallowRef({}); // same as processedTokens but filtered by format, theme and search
const filteredHeaders = shallowRef([]); // to fill the dynamic table of contents
const { headers } = inject('headers');

const noSearchResults = computed(() => filteredTokens.value === null);
const themeKey = computed(() => `${theme.value}-${mode.value}`);

const filterTokens = () => {
  if (!searchCriteria.value) {
    filteredTokens.value = structuredClone(processedTokens[format.value][themeKey.value]);
    updateHeaders();
    return;
  }

  // Replace '/' and '-' characters for ' ' to allow to search by the name shown in Figma:
  // typography/label/md-plain should match var(--dt-typography-label-plain)
  // or dtTypographyLabelMdPlain
  const searchValues = searchCriteria.value.replace(/[/-]/g, ' ').split(' ');
  const searchRegexArray = searchValues.map(value => value.replace(/\(/, '\\(').replace(/\)/, '\\)')); // escape parenthesis
  const regexArray = searchRegexArray.map(searchRegex => new RegExp(searchRegex, 'i'));
  filteredTokens.value = filterTokenNode(processedTokens[format.value][themeKey.value], null, regexArray);
  updateHeaders();
};

const filterTokenNode = (node, name, regexArray) => {
  const subNodesRet = {};
  let childrenRet = [];
  const matchingRegex = [];
  const nonMatchingRegex = [];
  regexArray.forEach(regex => {
    if (regex.test(name)) matchingRegex.push(regex);
    else nonMatchingRegex.push(regex);
  });
  // If the name matches all the terms, return the entire node
  if (nonMatchingRegex.length === 0) {
    return node;
    // else return the children that match
  } else if (node._children) {
    childrenRet = node._children.filter(token => !token.hidden).filter(token => {
      const { name: tokenName, tokenValue, keywords } = token;
      return nonMatchingRegex.every(regex => regex.test(tokenName) || regex.test(tokenValue) || regex.test(keywords));
    });
  }
  // look for other categories in the same level and filter recursively
  Object.keys(node).forEach(key => {
    if (key !== '_children') {
      const subNode = filterTokenNode(node[key], key, nonMatchingRegex);
      if (subNode !== null) subNodesRet[key] = subNode;
    }
  });
  if (Object.keys(subNodesRet).length > 0 || childrenRet.length > 0) {
    return {
      _children: childrenRet,
      ...subNodesRet,
    };
  }
  return null;
};

const updateHeaders = () => {
  if (filteredTokens.value === null) return [];
  filteredHeaders.value = updateHeadersRecursively(filteredTokens.value, null);
  headers.value = filteredHeaders.value;
};

const updateHeadersRecursively = (node, category) => {
  return Object.keys(node)
    .filter(subNodeKey => subNodeKey !== '_children')
    .map(subNodeKey => {
      return {
        title: capitalize(subNodeKey),
        level: 2,
        slug: category === null ? subNodeKey : `${category}-${subNodeKey}`,
        get link () {
          return `#${this.slug}`;
        },
        children: updateHeadersRecursively(node[subNodeKey], category === null ? subNodeKey : category),
      };
    });
};

onBeforeMount(() => {
  addTokensToStructure(processedTokens);
  filterTokens();
});
</script>
